import Tool from "./Tool";

export default class PanZoom extends Tool {
    constructor(editor, maxZoom = 5, minZoom = 1, crossBorders) {
        super(editor);
        this.minZoom = minZoom; // Minimum zoom level
        this.maxZoom = maxZoom; // Maximum zoom level
        this.zoom = 1;
        this.zoomFactor = 1.1;
        this.isDragging = false;
        this.waitDragging = false;
        this.dragThreshold = 3;

        this.dragStart = {
            x: 0,
            y: 0,
        };

        this.shortcut = true;
        this.isAnimating = false;

        this.centering = {
            dafault: 50,
            x: 0,
            y: 0,
        };
        this.currentTranslate = {
            x: 0,
            y: 0,
        };
        this.cachedTranslate = {
            x: 0,
            y: 0,
            scale: 1,
        };
        this.lastMoveClientCoords = {
            x: null,
            y: null,
        };
        this.canCrossBorders = crossBorders;
        this.shortcutZoom = this.shortcutZoom.bind(this);
        this.animationFrameId = null;
        this.transformAnimationFrameId = null;

        this.defaultAnimationDuration = 150;
    }

    getShortcuts() {
        return [
            {
                keyCombo: ["Space", "Equal"],
                toolConfig: {
                    context: "zoomIn",
                    instant: true,
                    action: this.shortcutZoom,
                },
            },
            {
                keyCombo: ["Space", "Minus"],
                toolConfig: {
                    context: "zoomOut",
                    instant: true,
                    action: this.shortcutZoom,
                },
            },

            {
                keyCombo: ["Space"],
                toolConfig: {
                    context: "move",
                    instant: false,
                    action: "setShortcutTool",
                    data: {
                        name: "panZoom",
                        context: "move",
                    },
                },
            },
            {
                keyCombo: ["Space", "Digit0"],
                toolConfig: {
                    context: "reset",
                    instant: true,
                    action: this.shortcutZoom,
                },
            },
        ];
    }

    setInitialCentering(centering = 50) {
        this.centering.default = centering;
        const canvas = this.editor.$refs.canvas;
        const frame = this.editor.$refs.frame;
        const canvasBounds = canvas.getBoundingClientRect();
        const frameBounds = frame.getBoundingClientRect();

        if (frameBounds.width > canvasBounds.width) {
            const excessWidth = (frameBounds.width - canvasBounds.width) / 2;
            this.currentTranslate.x = excessWidth;
            if (centering < 50) {
                const leftShift = (50 - centering) / 50;
                this.currentTranslate.x = leftShift * excessWidth;
            } else if (centering > 50) {
                const rightShift = (centering - 50) / 50;
                this.currentTranslate.x = -rightShift * excessWidth;
            } else {
                this.currentTranslate.x = 0;
            }
            this.centering.x = this.currentTranslate.x;
            this.centering.y = this.currentTranslate.y;
            this.cachedTranslate = {
                x: this.currentTranslate.x,
                y: this.currentTranslate.y,
                scale: this.zoom,
            };
            this.updateTransform();
        }
    }

    setCenter(centering) {
        const canvas = this.editor.$refs.canvas;
        const frame = this.editor.$refs.frame;
        const canvasBounds = canvas.getBoundingClientRect();
        const frameBounds = frame.getBoundingClientRect();

        if (frameBounds.width > canvasBounds.width) {
            const excessWidth = (frameBounds.width - canvasBounds.width) / 2;
            this.currentTranslate.x = excessWidth;
            if (centering < 50) {
                const leftShift = (50 - centering) / 50;
                this.currentTranslate.x = leftShift * excessWidth;
            } else if (centering > 50) {
                const rightShift = (centering - 50) / 50;
                this.currentTranslate.x = -rightShift * excessWidth;
            } else {
                this.currentTranslate.x = 0;
            }
            this.updateTransform();
        }
    }

    reset() {
        this.zoom = 1;
        this.isAnimating = false;
        this.isDragging = false;
        this.waitDragging = false;
        this.dragThreshold = 5;
        this.editor.functions.setZoom(this.zoom);

        this.centering = {
            dafault: 50,
            x: 0,
            y: 0,
        };
        this.currentTranslate = {
            x: 0,
            y: 0,
        };

        this.setTransform(
            this.currentTranslate.x,
            this.currentTranslate.y,
            this.zoom
        );
    }

    hasTransform() {
        return (
            this.currentTranslate.x !== 0 ||
            this.currentTranslate.y !== 0 ||
            this.zoom !== 1
        );
    }

    hasZoomTransform() {
        return this.zoom !== 1;
    }

    hasTranslateTransform() {
        return {
            x: this.currentTranslate.x !== 0,
            y: this.currentTranslate.y !== 0,
        };
    }

    resetFrame(duration = this.defaultAnimationDuration) {
        this.zoom = this.minZoom;
        this.currentTranslate.x = this.centering.x;
        this.currentTranslate.y = this.centering.y;
        this.editor.functions.setZoom(this.zoom);
        this.updateTransform(duration);
    }

    resetZoom(duration = this.defaultAnimationDuration) {
        this.zoom = this.minZoom;
        this.editor.functions.setZoom(this.zoom);
        this.updateTransform(duration);
    }

    resetTranslate(
        duration = this.defaultAnimationDuration,
        x = true,
        y = true
    ) {
        if (x) {
            this.currentTranslate.x = this.centering.x;
        }
        if (y) {
            this.currentTranslate.y = this.centering.y;
        }
        this.updateTransform(duration);
    }

    handleClick(data) {
        if (data.context === "move") return;

        const zoomIn = data.context === "zoomIn";

        const factor = zoomIn ? this.zoomFactor : 1 / this.zoomFactor;
        const container = this.editor.$refs.frame;
        const pointer = {
            x: data.event.clientX - container.offsetLeft,
            y: data.event.clientY - container.offsetTop,
        };

        this.processZoom(pointer, factor, this.defaultAnimationDuration);
    }

    animateZoom(layer, factor, animationTime = 0) {
        const pointer = layer.tool.getCenterCoords(layer);
        pointer.x = pointer.x / this.editor.getters.getAspectRatio();
        pointer.y = pointer.y / this.editor.getters.getAspectRatio();
        this.processZoom(pointer, factor, animationTime);
    }

    wheelScroll(data) {
        let deltaX = data.event.deltaX;
        let deltaY = data.event.deltaY;
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }

        this.animationFrameId = requestAnimationFrame(() => {
            let maxDistance = this.getMaxDistance();
            let { wider: canvasWider, taller: canvasTaller } =
                this.isCanvasLargerThanFrame();

            let reverseX = this.canCrossBorders ? false : canvasWider;
            let reverseY = this.canCrossBorders ? false : canvasTaller;

            if (this.getScrollDirection(deltaX, reverseX) < 0) {
                deltaX =
                    Math.sign(deltaX) *
                    Math.min(Math.abs(deltaX), maxDistance.right);
            }
            if (this.getScrollDirection(deltaX, reverseX) > 0) {
                deltaX =
                    Math.sign(deltaX) *
                    Math.min(Math.abs(deltaX), maxDistance.left);
            }

            if (this.getScrollDirection(deltaY, reverseY) > 0) {
                deltaY =
                    Math.sign(deltaY) *
                    Math.min(Math.abs(deltaY), maxDistance.top);
            }

            if (this.getScrollDirection(deltaY, reverseY) < 0) {
                deltaY =
                    Math.sign(deltaY) *
                    Math.min(Math.abs(deltaY), maxDistance.bottom);
            }

            deltaX = Math.round(deltaX);
            deltaY = Math.round(deltaY);
            if (deltaX != 0 || deltaY != 0) {
                this.currentTranslate.x -= deltaX;
                this.currentTranslate.y -= deltaY;
                this.updateTransform();
            }
        });
    }

    wheelZoom(data) {
        const zoomIn = data.event.deltaY < 0;
        const factor = zoomIn ? this.zoomFactor : 1 / this.zoomFactor;
        const container = this.editor.$refs.frame;
        const canvas = this.editor.$refs.canvas;
        const canvasRect = canvas.getBoundingClientRect();
        const relationDeltaX = canvasRect.left - canvas.offsetLeft;
        const relationDeltaY = canvasRect.top - canvas.offsetTop;
        const pointer = {
            x: data.event.clientX - container.offsetLeft - relationDeltaX,
            y: data.event.clientY - container.offsetTop - relationDeltaY,
        };

        this.processZoom(pointer, factor);
    }

    shortcutZoom(data) {
        if (data.context === "reset") {
            this.resetFrame();
        }
        if (data.context === "move") return;
        const zoomIn = data.context === "zoomIn";

        const factor = zoomIn ? this.zoomFactor : 1 / this.zoomFactor;
        let pointer = null;

        if (data.layer) {
            pointer = data.layer.tool.getCenterCoords(data.layer);
            pointer.x = pointer.x / this.editor.getters.getAspectRatio();
            pointer.y = pointer.y / this.editor.getters.getAspectRatio();
        }
        if (!pointer) {
            pointer = this.getContainerCenterCoords();
        }

        this.processZoom(pointer, factor, this.defaultAnimationDuration);
    }
    // animationTransition - 0 to disable animations
    processZoom(pointer, factor, animationTransition = 0) {
        let newZoom = parseFloat((this.zoom * factor).toFixed(2));

        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }

        if (newZoom < this.minZoom) {
            this.zoom = this.minZoom;
            newZoom = this.zoom;
            factor = 1;
            if (this.canCrossBorders) {
                this.resetFrame();
                return;
            }
        }

        this.animationFrameId = requestAnimationFrame(() => {
            if (newZoom > this.maxZoom) {
                newZoom = this.maxZoom;
                factor = newZoom / this.zoom;
            }

            const { offsetX, offsetY } = this.calculatePointerOffset(pointer);
            let { predictedTranslateX, predictedTranslateY } =
                this.calculatePredictedTranslate(offsetX, offsetY, factor);
            predictedTranslateX = Math.round(predictedTranslateX);
            predictedTranslateY = Math.round(predictedTranslateY);

            let finalTranslateX = predictedTranslateX;
            let finalTranslateY = predictedTranslateY;

            if (!this.canCrossBorders) {
                const predictedFrameBounds = this.calculatePredictedFrameBounds(
                    predictedTranslateX,
                    predictedTranslateY,
                    newZoom
                );

                const { wider: canvasWider, taller: canvasTaller } =
                    this.isCanvasLargerThanFrame(predictedFrameBounds);
                const outside =
                    this.getDistanceToOutsideBounds(predictedFrameBounds);
                const inside =
                    this.getDistanceToInsideBounds(predictedFrameBounds);

                if (canvasTaller) {
                    if (inside.top > 0) {
                        finalTranslateY += inside.top;
                    } else if (inside.bottom > 0) {
                        finalTranslateY -= inside.bottom;
                    }
                } else {
                    if (outside.top > 0) {
                        finalTranslateY -= outside.top;
                    } else if (outside.bottom > 0) {
                        finalTranslateY += outside.bottom;
                    }
                }

                if (canvasWider) {
                    if (inside.left > 0) {
                        finalTranslateX += inside.left;
                    } else if (inside.right > 0) {
                        finalTranslateX -= inside.right;
                    }
                } else {
                    if (outside.left > 0) {
                        finalTranslateX -= outside.left;
                    } else if (outside.right > 0) {
                        finalTranslateX += outside.right;
                    }
                }
            }

            this.currentTranslate.x = Math.round(finalTranslateX);
            this.currentTranslate.y = Math.round(finalTranslateY);
            this.zoom = newZoom;

            this.updateTransform(animationTransition);
            this.editor.functions.setZoom(this.zoom);
        });
    }

    keepFrameInBounds(deltaX = 0, deltaY = 0, animationTransition = 0) {
        let { wider: canvasWider, taller: canvasTaller } =
            this.isCanvasLargerThanFrame();

        const outside = this.getDistanceToOutsideBounds();
        const inside = this.getDistanceToInsideBounds();

        if (deltaX === 0) {
            if (!canvasWider) {
                if (outside.left > 0) {
                    deltaX -= outside.left;
                }
                if (outside.right > 0) {
                    deltaX += outside.right;
                }
            } else {
                if (inside.left > 0) {
                    deltaX += inside.left;
                }
                if (inside.right > 0) {
                    deltaX -= inside.right;
                }
            }
        }
        if (deltaY === 0) {
            if (!canvasTaller) {
                if (outside.top > 0) {
                    deltaY -= outside.top;
                }
                if (outside.bottom > 0) {
                    deltaY += outside.bottom;
                }
            } else {
                if (inside.top > 0) {
                    deltaY += inside.top;
                }
                if (inside.bottom > 0) {
                    deltaY -= inside.bottom;
                }
            }
        }
        if (deltaX != 0 || deltaY != 0) {
            this.currentTranslate.x += deltaX;
            this.currentTranslate.y += deltaY;
            this.updateTransform(animationTransition);
        }
    }

    handleMouseDown(data) {
        if (data.context !== "move") return;

        this.waitDragging = true;
        this.dragStart.x = data.event.clientX;
        this.dragStart.y = data.event.clientY;
    }

    handleMouseMove(data) {
        if (this.waitDragging) {
            const deltaX = data.event.clientX - this.dragStart.x;
            const deltaY = data.event.clientY - this.dragStart.y;

            if (
                Math.abs(deltaX) > this.dragThreshold ||
                Math.abs(deltaY) > this.dragThreshold
            ) {
                this.isDragging = true;
                this.waitDragging = false;
            }
        }
        console.log(this.isDragging);

        if (!this.isDragging) return;

        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }

        this.animationFrameId = requestAnimationFrame(() => {
            let { wider: canvasWider, taller: canvasTaller } =
                this.isCanvasLargerThanFrame();
            let deltaX = data.event.clientX - this.dragStart.x;
            let deltaY = data.event.clientY - this.dragStart.y;
            const maxDistance = this.getMaxDistance();

            let reverseX = this.canCrossBorders ? true : !canvasWider;
            let reverseY = this.canCrossBorders ? true : !canvasTaller;

            if (this.getScrollDirection(deltaX, reverseX) < 0) {
                deltaX =
                    Math.sign(deltaX) *
                    Math.min(Math.abs(deltaX), maxDistance.right);
            }
            if (this.getScrollDirection(deltaX, reverseX) > 0) {
                deltaX =
                    Math.sign(deltaX) *
                    Math.min(Math.abs(deltaX), maxDistance.left);
            }

            if (this.getScrollDirection(deltaY, reverseY) > 0) {
                deltaY =
                    Math.sign(deltaY) *
                    Math.min(Math.abs(deltaY), maxDistance.top);
            }

            if (this.getScrollDirection(deltaY, reverseY) < 0) {
                deltaY =
                    Math.sign(deltaY) *
                    Math.min(Math.abs(deltaY), maxDistance.bottom);
            }

            deltaX = Math.round(deltaX);
            deltaY = Math.round(deltaY);

            if (deltaX != 0 || deltaY != 0) {
                this.currentTranslate.x += deltaX;
                this.currentTranslate.y += deltaY;
                this.updateTransform();
            }
            this.dragStart.x = data.event.clientX;
            this.dragStart.y = data.event.clientY;
        });
    }

    handleToolChanged(tool, newTool, layer, shortcut) {
        this.isDragging = false;
        this.waitDragging = false;
    }

    handleMouseUp() {
        this.isDragging = false;
        this.waitDragging = false;

        // if (data.context !== "move") return;
    }

    handleMouseLeave() {
        this.isDragging = false;
        this.waitDragging = false;
    }

    updateTransform(animationTransition = 0) {
        if (this.isAnimating) return;

        const translateX = this.currentTranslate.x;
        const translateY = this.currentTranslate.y;

        if (!this.isDragging && animationTransition) {
            this.animateTransform(
                translateX,
                translateY,
                this.zoom,
                animationTransition
            );
            return;
        }
        this.setTransform(translateX, translateY, this.zoom);
    }

    setTransform(x, y, scale) {
        if (this.transformAnimationFrameId) {
            cancelAnimationFrame(this.transformAnimationFrameId);
        }

        this.transformAnimationFrameId = requestAnimationFrame(() => {
            this.editor.$refs.canvasLayers.style.transform = `translate(${x}px, ${y}px) scale(${scale}) `;
            this.editor.$refs.frame.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
            this.editor.functions.transformCallback(
                {
                    x,
                    y,
                    scale,
                },
                this.cachedTranslate
            );
            this.cachedTranslate = {
                x,
                y,
                scale,
            };
        });
    }

    animateTransform(targetX, targetY, targetScale, duration) {
        this.isAnimating = true;

        const startX = this.cachedTranslate.x;
        const startY = this.cachedTranslate.y;
        const startScale = this.cachedTranslate.scale;

        const deltaX = targetX - startX;
        const deltaY = targetY - startY;
        const deltaScale = targetScale - startScale;

        let startTime = null;

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            const translateX = startX + deltaX * progress;
            const translateY = startY + deltaY * progress;
            const scale = startScale + deltaScale * progress;

            this.setTransform(translateX, translateY, scale);

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                this.isAnimating = false;
            }
        };

        requestAnimationFrame(animate);
    }

    getMaxDistance(frameBounds = null) {
        frameBounds = frameBounds ?? this.getFrameBounds();

        const {
            left: outsideLeft,
            right: outsideRight,
            top: outsideTop,
            bottom: outsideBottom,
        } = this.getDistanceToOutsideBounds(frameBounds);

        const {
            left: insideLeft,
            right: insideRight,
            top: insideTop,
            bottom: insideBottom,
        } = this.getDistanceToInsideBounds(frameBounds);

        const { wider: canvasWider, taller: canvasTaller } =
            this.isCanvasLargerThanFrame(frameBounds);

        let maxMove = {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
        };
        const threshold = -10;
        if (this.canCrossBorders) {
            maxMove.right = Math.abs(
                frameBounds.width - insideLeft - threshold
            );
            maxMove.left = Math.abs(
                insideRight - frameBounds.width + threshold
            );
            maxMove.bottom = Math.abs(
                frameBounds.height - insideTop - threshold
            );
            maxMove.top = Math.abs(
                insideBottom - frameBounds.height + threshold
            );
        } else {
            if (canvasTaller) {
                maxMove.bottom = outsideBottom;
                maxMove.top = outsideTop;
            } else {
                maxMove.bottom = insideBottom;
                maxMove.top = insideTop;
            }

            if (canvasWider) {
                maxMove.right = outsideRight;
                maxMove.left = outsideLeft;
            } else {
                maxMove.right = insideRight;
                maxMove.left = insideLeft;
            }
        }
        return maxMove;
    }

    isCanvasLargerThanFrame(frameBounds = null) {
        const canvas = this.editor.$refs.canvas;
        const canvasBounds = canvas.getBoundingClientRect();
        frameBounds = frameBounds ?? this.getFrameBounds();

        return {
            wider: canvasBounds.width >= frameBounds.width,
            taller: canvasBounds.height >= frameBounds.height,
        };
    }

    getDistanceToInsideBounds(frameBounds = null) {
        frameBounds = frameBounds ?? this.getFrameBounds();

        const canvas = this.editor.$refs.canvas;
        const canvasBounds = canvas.getBoundingClientRect();

        // Расстояние до внутренних границ
        const distanceLeft = Math.max(0, canvasBounds.left - frameBounds.left);
        const distanceRight = Math.max(
            0,
            frameBounds.right - canvasBounds.right
        );
        const distanceTop = Math.max(0, canvasBounds.top - frameBounds.top);
        const distanceBottom = Math.max(
            0,
            frameBounds.bottom - canvasBounds.bottom
        );

        return {
            left: distanceLeft,
            right: distanceRight,
            top: distanceTop,
            bottom: distanceBottom,
        };
    }

    getDistanceToOutsideBounds(frameBounds = null) {
        frameBounds = frameBounds ?? this.getFrameBounds();
        const canvas = this.editor.$refs.canvas;
        const canvasBounds = canvas.getBoundingClientRect();

        // Расстояние до внешних границ
        const distanceLeft = Math.max(0, frameBounds.left - canvasBounds.left);
        const distanceRight = Math.max(
            0,
            canvasBounds.right - frameBounds.right
        );
        const distanceTop = Math.max(0, frameBounds.top - canvasBounds.top);
        const distanceBottom = Math.max(
            0,
            canvasBounds.bottom - frameBounds.bottom
        );

        return {
            left: distanceLeft,
            right: distanceRight,
            top: distanceTop,
            bottom: distanceBottom,
        };
    }

    calculateBoundingBox() {
        const width = this.editor.getters.getCalculatedWidth() * this.zoom;
        const height = this.editor.getters.getCalculatedHeight() * this.zoom;
        return {
            left: this.currentTranslate.x - width / 2,
            top: this.currentTranslate.y - height / 2,
            right: this.currentTranslate.x + width / 2,
            bottom: this.currentTranslate.y + height / 2,
            width,
            height,
        };
    }

    getFrameBounds() {
        const frame = this.editor.$refs.frame;
        const rect = frame.getBoundingClientRect();

        return {
            left: rect.left,
            right: rect.right,
            top: rect.top,
            bottom: rect.bottom,
            width: rect.width,
            height: rect.height,
        };
    }

    getScrollDirection(delta, inverted = false) {
        return inverted ? Math.sign(delta) : -Math.sign(delta);
    }

    getContainerCenterCoords() {
        const imgCenterX = this.editor.getters.getCalculatedWidth() / 2;
        const imgCenterY = this.editor.getters.getCalculatedHeight() / 2;
        return { x: imgCenterX, y: imgCenterY };
    }

    calculatePointerOffset(pointer) {
        return {
            offsetX: pointer.x - this.editor.getters.getCalculatedWidth() / 2,
            offsetY: pointer.y - this.editor.getters.getCalculatedHeight() / 2,
        };
    }

    calculatePredictedTranslate(offsetX, offsetY, factor) {
        return {
            predictedTranslateX: Math.round(
                offsetX - (offsetX - this.currentTranslate.x) * factor
            ),
            predictedTranslateY: Math.round(
                offsetY - (offsetY - this.currentTranslate.y) * factor
            ),
        };
    }

    calculatePredictedFrameBounds(
        predictedTranslateX,
        predictedTranslateY,
        newZoom
    ) {
        const currentFrameBounds = this.getFrameBounds();

        const predictedWidth = (currentFrameBounds.width / this.zoom) * newZoom;
        const predictedHeight =
            (currentFrameBounds.height / this.zoom) * newZoom;

        const deltaWidth = currentFrameBounds.width - predictedWidth;
        const deltaHeight = currentFrameBounds.height - predictedHeight;

        const predictedLeft =
            currentFrameBounds.left +
            deltaWidth / 2 +
            (predictedTranslateX - this.currentTranslate.x);
        const predictedTop =
            currentFrameBounds.top +
            deltaHeight / 2 +
            (predictedTranslateY - this.currentTranslate.y);
        return {
            left: predictedLeft,
            top: predictedTop,
            right: predictedLeft + predictedWidth,
            bottom: predictedTop + predictedHeight,
            width: predictedWidth,
            height: predictedHeight,
        };
    }
}
